'use client';

import { useSyncExternalStore } from 'react';
import { Link } from '@/i18n/navigation';

declare global {
  interface Window {
    _mtm?: Array<Record<string, unknown>>;
  }
}

type ConsentState = 'not-shown' | 'shown' | 'accepted-all' | 'accepted-essential';

const STORAGE_KEY = 'cookie-consent';
const CHANGE_EVENT = 'cookie-consent-change';

function subscribe(callback: () => void) {
  window.addEventListener('storage', callback);
  window.addEventListener(CHANGE_EVENT, callback);
  return () => {
    window.removeEventListener('storage', callback);
    window.removeEventListener(CHANGE_EVENT, callback);
  };
}

function getSnapshot(): ConsentState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (stored as ConsentState) : 'shown';
  } catch {
    // localStorage unavailable (e.g. Firefox private mode SecurityError)
    return 'not-shown';
  }
}

function getServerSnapshot(): ConsentState {
  return 'not-shown';
}

function setConsent(state: ConsentState) {
  try {
    localStorage.setItem(STORAGE_KEY, state);
  } catch {
    // localStorage unavailable — consent won't persist, but don't crash
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function CookieConsent() {
  const consentState = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const handleAcceptAll = () => {
    setConsent('accepted-all');

    // Enable Matomo analytics
    if (window.location.hostname !== 'localhost' && window._mtm) {
      window._mtm.push({ 'event': 'consentSetup', 'consentType': 'all' });
    }
  };

  const handleOnlyEssential = () => {
    setConsent('accepted-essential');
  };

  // Don't show if consent already given or not yet loaded
  if (consentState !== 'shown') {
    return null;
  }

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-40 bg-dark-surface text-white p-4 sm:p-6 border-t border-gray-600"
    >
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-semibold mb-2">Cookie Consent</h3>
            <p className="text-sm text-gray-400 mb-3">
              We use cookies to enhance your experience and analyze site usage. Essential cookies are required for basic functionality. Analytics cookies help us improve Vetta. Please{' '}
              <Link
                href="/privacy"
                className="underline hover:text-gray-200 transition-colors"
              >
                read our privacy policy
              </Link>{' '}
              for details on the cookies we use and their purposes.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 flex-shrink-0">
            <button
              onClick={handleOnlyEssential}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-white hover:bg-gray-600 transition-colors"
              aria-label="Accept only essential cookies"
            >
              Only Essential
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-teal-500 text-white hover:bg-teal-600 transition-colors"
              aria-label="Accept all cookies"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
